import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import "./ChatbotCF.css";
import Repo from "../Utility/Repo";
import { TokenContext } from "./TokenContext";
import {
  validateCodiceFiscale,
  decodeCodiceFiscale,
} from "../Utility/codiceFiscaleUtils";

// ─────────────────────────────────────────────────────────────────────────────
//  NLP – motore intent con scoring pesato, stemming approssimato, multi-intent
// ─────────────────────────────────────────────────────────────────────────────

// Normalizzazione: rimuove accenti, apostrofi, punteggiatura; lowercase
function normalize(text) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[''`]/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Stemming italiano approssimato (rimuove suffissi comuni)
function stem(word) {
  return (
    word
      .replace(
        /(azioni|azione|amenti|amento|ioni|ione|isti|ista|ismo|ente|enti|ando|endo|are|ere|ire|ato|uto|ito|ata|uta|ita|ano|ono|ino|eto|ini|ale|ali|osi|ose|osa|oso)$/,
        "",
      )
      .replace(/(zioni|zione|tori|tore|ture|tura)$/, "") || word
  );
}

// Dizionario intent con keyword e pesi
const INTENT_DICT = {
  isee: {
    label: "ISEE",
    terms: [
      { w: "isee", score: 10 },
      { w: "reddito", score: 7 },
      { w: "fascia", score: 6 },
      { w: "situazione economica", score: 8 },
      { w: "dichiarazione", score: 5 },
      { w: "guadagn", score: 6 },
      { w: "entrate", score: 5 },
      { w: "economica", score: 5 },
      { w: "valore", score: 4 },
      { w: "attestazione", score: 7 },
      { w: "patrimonio", score: 6 },
      { w: "inps", score: 6 },
    ],
  },
  iscrizioni: {
    label: "Iscrizioni",
    terms: [
      { w: "iscriz", score: 10 },
      { w: "serviz", score: 7 },
      { w: "intervent", score: 7 },
      { w: "domand", score: 6 },
      { w: "request", score: 5 },
      { w: "richiest", score: 7 },
      { w: "partecip", score: 6 },
      { w: "iscritto", score: 9 },
      { w: "bando", score: 7 },
      { w: "annuncio", score: 6 },
      { w: "pratich", score: 5 },
      { w: "elenco servizi", score: 8 },
    ],
  },
  pagamenti: {
    label: "Pagamenti",
    terms: [
      { w: "pagament", score: 10 },
      { w: "bollett", score: 9 },
      { w: "quota", score: 7 },
      { w: "pagato", score: 8 },
      { w: "debit", score: 7 },
      { w: "importo", score: 7 },
      { w: "fattur", score: 6 },
      { w: "versament", score: 7 },
      { w: "deve pagare", score: 9 },
      { w: "pagoPA", score: 9 },
      { w: "iuv", score: 9 },
      { w: "soldi", score: 5 },
      { w: "euro", score: 4 },
    ],
  },
  anagrafica: {
    label: "Dati anagrafici",
    terms: [
      { w: "anagrafi", score: 10 },
      { w: "dati person", score: 9 },
      { w: "chi e", score: 7 },
      { w: "nome", score: 6 },
      { w: "cognome", score: 6 },
      { w: "indirizzo", score: 7 },
      { w: "telefon", score: 7 },
      { w: "email", score: 7 },
      { w: "contatt", score: 6 },
      { w: "residenza", score: 7 },
      { w: "comune", score: 5 },
      { w: "account", score: 5 },
    ],
  },
  allegati: {
    label: "Allegati/Documenti",
    terms: [
      { w: "allegat", score: 10 },
      { w: "document", score: 9 },
      { w: "file", score: 7 },
      { w: "pdf", score: 8 },
      { w: "caricato", score: 6 },
      { w: "upload", score: 7 },
      { w: "cert", score: 5 },
    ],
  },
  statistiche: {
    label: "Statistiche",
    terms: [
      { w: "statistic", score: 10 },
      { w: "riepilog", score: 8 },
      { w: "riassunt", score: 8 },
      { w: "tutto", score: 6 },
      { w: "completo", score: 7 },
      { w: "sintesi", score: 8 },
      { w: "panoramic", score: 8 },
      { w: "quant", score: 5 },
      { w: "totale", score: 6 },
      { w: "overview", score: 7 },
      { w: "dettagl", score: 6 },
    ],
  },
};

// Domande interrogative che non contribuiscono all'intent ma indicano una domanda
const QUESTION_WORDS = [
  "quant",
  "qual",
  "cosa",
  "come",
  "quando",
  "dimmi",
  "mostr",
  "hai",
  "ha",
  "ci sono",
  "c e",
  "elenc",
  "vorrei sapere",
  "puoi dirmi",
  "sai se",
  "fammi vedere",
];

function detectIntent(rawText) {
  const text = normalize(rawText);
  const tokens = text.split(" ").map(stem);
  const scores = {};

  for (const [key, def] of Object.entries(INTENT_DICT)) {
    let score = 0;
    for (const { w, score: pts } of def.terms) {
      const wNorm = normalize(w);
      // match su testo originale normalizzato (per frasi multi-parola)
      if (text.includes(wNorm)) {
        score += pts;
        continue;
      }
      // match su singolo token stemmato
      const wStem = stem(wNorm);
      if (tokens.some((t) => t.startsWith(wStem) || wStem.startsWith(t))) {
        score += pts * 0.85;
      }
    }
    scores[key] = score;
  }

  const ranked = Object.entries(scores)
    .filter(([, s]) => s > 0)
    .sort((a, b) => b[1] - a[1]);

  if (!ranked.length) return null;

  // Se punteggio top è molto basso (< 4) e c'è una parola interrogativa, suggerisci help
  const topScore = ranked[0][1];
  const hasQuestion = QUESTION_WORDS.some((q) => text.includes(normalize(q)));
  if (topScore < 4 && !hasQuestion) return null;

  // Multi-intent: ritorna array se ci sono 2+ intent con punteggio vicino (entro 30%)
  const topKey = ranked[0][0];
  const secondClose = ranked[1] && ranked[1][1] >= topScore * 0.7;
  if (secondClose) return [topKey, ranked[1][0]];

  return topKey;
}

const STATE_LABELS = {
  VALIDATED: "Accettata",
  REFUSED: "Rifiutata",
  SUSPENDED: "Sospesa",
  ADDED: "Inviata",
  PAGAMENTO: "Con pagamento",
  LOADED: "Inviata",
};

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────
const CF_REGEX = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;

function nowHHMM() {
  return new Date().toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function makeId() {
  return Math.random().toString(36).slice(2);
}

// ─────────────────────────────────────────────────────────────────────────────
//  Sub‑components
// ─────────────────────────────────────────────────────────────────────────────
function TypingBubble() {
  return (
    <div className="message-row bot">
      <div className="message-avatar">🤖</div>
      <div className="typing-bubble">
        <div className="typing-dot" />
        <div className="typing-dot" />
        <div className="typing-dot" />
      </div>
    </div>
  );
}

function TextBubble({ text, sender, time }) {
  return (
    <div className={`message-row ${sender}`}>
      {sender === "bot" && <div className="message-avatar">🤖</div>}
      <div>
        <div className="message-bubble">{text}</div>
        {time && <div className="message-meta">{time}</div>}
      </div>
    </div>
  );
}

function SummaryCard({ cf, decoded, systemUser, validation }) {
  return (
    <div className="message-row bot" style={{ alignItems: "flex-start" }}>
      <div className="message-avatar">🤖</div>
      <div className="summary-card">
        {/* ── Header ── */}
        <div className="summary-card-header">
          📋 Riepilogo&nbsp;—&nbsp;
          <span style={{ fontFamily: "monospace", letterSpacing: 1 }}>
            {cf}
          </span>
        </div>

        {/* ── Validità CF ── */}
        <div className="summary-card-section">
          <div className="summary-card-section-title">
            Validità codice fiscale
          </div>
          <div className="summary-card-row">
            <span className="summary-card-label">Stato:</span>
            {validation.valid ? (
              <span className="summary-badge valid">✔ Valido</span>
            ) : (
              <span className="summary-badge invalid">
                ✖ Non valido — {validation.reason}
              </span>
            )}
          </div>
        </div>

        {/* ── Dati estratti dal CF ── */}
        {decoded && (
          <div className="summary-card-section">
            <div className="summary-card-section-title">
              Dati estratti dal codice fiscale
            </div>
            <div className="summary-card-row">
              <span className="summary-card-label">Data di nascita:</span>
              <span className="summary-card-value">
                {decoded.birthDateFormatted}
              </span>
            </div>
            <div className="summary-card-row">
              <span className="summary-card-label">Sesso:</span>
              <span className="summary-card-value">{decoded.gender}</span>
            </div>
            <div className="summary-card-row">
              <span className="summary-card-label">Luogo di nascita:</span>
              <span className="summary-card-value">{decoded.birthPlace}</span>
            </div>
            {decoded.age !== null && (
              <div className="summary-card-row">
                <span className="summary-card-label">Età:</span>
                <span className="summary-card-value">{decoded.age} anni</span>
              </div>
            )}
          </div>
        )}

        {/* ── Dati nel sistema ── */}
        <div className="summary-card-section">
          <div className="summary-card-section-title">Utente nel sistema</div>
          {systemUser ? (
            <>
              <div className="summary-card-row">
                <span className="summary-card-label">Stato:</span>
                <span className="summary-badge found">✔ Trovato</span>
              </div>
              <div className="summary-card-row">
                <span className="summary-card-label">Nome:</span>
                <span className="summary-card-value">
                  {systemUser.firstName || "—"}
                </span>
              </div>
              <div className="summary-card-row">
                <span className="summary-card-label">Cognome:</span>
                <span className="summary-card-value">
                  {systemUser.lastName || "—"}
                </span>
              </div>
              {systemUser.email && (
                <div className="summary-card-row">
                  <span className="summary-card-label">Email:</span>
                  <span className="summary-card-value">{systemUser.email}</span>
                </div>
              )}
              {systemUser.phoneNumber && (
                <div className="summary-card-row">
                  <span className="summary-card-label">Telefono:</span>
                  <span className="summary-card-value">
                    {systemUser.phoneNumber}
                  </span>
                </div>
              )}
              {systemUser.city && (
                <div className="summary-card-row">
                  <span className="summary-card-label">Comune:</span>
                  <span className="summary-card-value">{systemUser.city}</span>
                </div>
              )}
              {systemUser.isEnabled !== undefined && (
                <div className="summary-card-row">
                  <span className="summary-card-label">Account attivo:</span>
                  <span className="summary-card-value">
                    {systemUser.isEnabled ? "Sì" : "No"}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="summary-card-row">
              <span className="summary-card-label">Stato:</span>
              <span className="summary-badge not-found">
                ⚠ Non presente nel sistema
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────────
const GREETING = [
  "Ciao! Sono l'assistente del portale Servizi alla Persona.",
  "Inserisci un codice fiscale (16 caratteri) per ottenere un riepilogo completo dell'utente.",
];

const HELP_TEXT =
  "Cosa posso fare:\n\n" +
  "🔍 Inserisci un CF di 16 caratteri per cercare un utente\n\n" +
  "Con un utente attivo puoi chiedermi in italiano naturale:\n" +
  '• ISEE \u2014 "quant\'è il suo isee?", "fascia di reddito"\n' +
  '• Iscrizioni \u2014 "ha iscrizioni?", "mostrami i servizi"\n' +
  '• Pagamenti \u2014 "ci sono bollette?", "ha debiti?"\n' +
  '• Anagrafica \u2014 "chi è?", "email e telefono"\n' +
  '• Documenti \u2014 "quali allegati ha?"\n' +
  '• Quadro completo \u2014 "tutto", "panoramica"\n\n' +
  'Puoi anche combinare: "iscrizioni e pagamenti"\n\n' +
  "NUOVO — cambia utente | AIUTO — questo messaggio";

function ChatbotCF() {
  const apiToken = useContext(TokenContext);

  // { id, type: "text"|"summary"|"typing", sender, text?, cf?, decoded?, systemUser?, validation?, time? }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // ── Contesto conversazionale ──
  const [activeUser, setActiveUser] = useState(null); // systemUser trovato
  const [activeCf, setActiveCf] = useState(null); // CF cercato
  const [activeDecoded, setActiveDecoded] = useState(null);
  const messagesRef = useRef(null); // ref al contenitore scroll
  const inputRef = useRef(null);

  // ── Initial greeting ────────────────────────────────────────
  useEffect(() => {
    const delay = (ms) => new Promise((res) => setTimeout(res, ms));
    let cancelled = false;

    (async () => {
      for (let i = 0; i < GREETING.length; i++) {
        if (cancelled) break;
        await delay(i === 0 ? 400 : 900);
        if (!cancelled) {
          setMessages((prev) => [
            ...prev,
            {
              id: makeId(),
              type: "text",
              sender: "bot",
              text: GREETING[i],
              time: nowHHMM(),
            },
          ]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Scroll to bottom on new messages (scroll the container, not the page) ──
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Helpers ─────────────────────────────────────────────────
  const addMsg = useCallback((msg) => {
    setMessages((prev) => [...prev, { id: makeId(), time: nowHHMM(), ...msg }]);
  }, []);

  const addTyping = useCallback(() => {
    const typingId = makeId();
    setMessages((prev) => [
      ...prev,
      { id: typingId, type: "typing", sender: "bot" },
    ]);
    return typingId;
  }, []);

  const removeTyping = useCallback((typingId) => {
    setMessages((prev) => prev.filter((m) => m.id !== typingId));
  }, []);

  const simulateDelay = (ms) => new Promise((res) => setTimeout(res, ms));

  // ── Intent handlers ──────────────────────────────────────────
  const handleIseeIntent = useCallback(
    async (user) => {
      if (!user) {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non ho un utente attivo. Inserisci prima un codice fiscale.",
        });
        return;
      }
      addMsg({
        type: "text",
        sender: "bot",
        text: `Sto recuperando i dati ISEE di ${user.firstName || ""} ${user.lastName || ""}…`,
      });
      try {
        const res = await Repo.getIseeListValidated(apiToken);
        const allIsee = res?.data || [];
        // filtra per fiscalCode dell'utente — il CF è in i.user.fiscalCode
        const userIsee = allIsee.filter(
          (i) =>
            i.user?.fiscalCode &&
            user.fiscalCode &&
            i.user.fiscalCode.toUpperCase() === user.fiscalCode.toUpperCase(),
        );
        if (!userIsee.length) {
          addMsg({
            type: "text",
            sender: "bot",
            text: "Nessun ISEE validato trovato per questo utente.",
          });
          return;
        }
        // ordina per anno desc
        const sorted = [...userIsee].sort(
          (a, b) => (b.year || 0) - (a.year || 0),
        );
        const latest = sorted[0];
        const valore = latest.value ?? "—";
        const anno = latest.year ?? "";
        const stato = latest.state ?? "";
        let risposta = `📊 ISEE di ${user.firstName || ""} ${user.lastName || ""}:\n`;
        risposta += `• Valore: ${typeof valore === "number" ? valore.toLocaleString("it-IT", { minimumFractionDigits: 2 }) + " €" : valore}\n`;
        if (anno) risposta += `• Anno di riferimento: ${anno}\n`;
        if (stato) risposta += `• Stato: ${stato}\n`;
        if (sorted.length > 1)
          risposta += `\n(${sorted.length} attestazioni in totale)`;
        addMsg({ type: "text", sender: "bot", text: risposta });
      } catch {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non riesco a recuperare le informazioni ISEE al momento.",
        });
      }
    },
    [apiToken, addMsg],
  );

  const handleIscrizioniIntent = useCallback(
    async (user) => {
      if (!user) {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non ho un utente attivo. Inserisci prima un codice fiscale.",
        });
        return;
      }
      addMsg({
        type: "text",
        sender: "bot",
        text: `Sto recuperando le iscrizioni di ${user.firstName || ""} ${user.lastName || ""}…`,
      });
      try {
        const res = await Repo.getSubscriptionUser(apiToken, user.id);
        const subs = res?.data || [];
        const list = Array.isArray(subs) ? subs : [subs];
        const valid = list.filter(Boolean);
        if (!valid.length) {
          addMsg({
            type: "text",
            sender: "bot",
            text: "Nessuna iscrizione trovata per questo utente.",
          });
          return;
        }
        let risposta = `📋 Iscrizioni di ${user.firstName || ""} ${user.lastName || ""} (${valid.length}):\n\n`;
        valid.forEach((s, idx) => {
          const servizio = s.announcement?.service?.name || "";
          const intervento = s.announcement?.name || "";
          const nome =
            servizio && intervento
              ? `${servizio} – ${intervento}`
              : servizio || intervento || `Iscrizione ${idx + 1}`;
          const protocollo = s.nProtocollo || "";
          const dataIscr = s.dataIscrizione || s.createdAt || "";
          const stato = STATE_LABELS[s.state] || s.state || "";
          risposta += `${idx + 1}. ${nome}\n`;
          if (protocollo) risposta += `   N. Protocollo: ${protocollo}\n`;
          if (dataIscr) risposta += `   Data iscrizione: ${dataIscr}\n`;
          if (stato) risposta += `   Stato: ${stato}\n`;
          risposta += "\n";
        });
        addMsg({ type: "text", sender: "bot", text: risposta.trimEnd() });
      } catch {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non riesco a recuperare le iscrizioni al momento.",
        });
      }
    },
    [apiToken, addMsg],
  );

  const handlePagamentiIntent = useCallback(
    async (user) => {
      if (!user) {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non ho un utente attivo. Inserisci prima un codice fiscale.",
        });
        return;
      }
      addMsg({
        type: "text",
        sender: "bot",
        text: `Sto recuperando i pagamenti di ${user.firstName || ""} ${user.lastName || ""}…`,
      });
      try {
        const res = await Repo.getPagamentiUtentiOperator(
          apiToken,
          user.fiscalCode,
        );
        const payments = res?.data || [];
        const list = Array.isArray(payments) ? payments : [payments];
        const valid = list.filter(Boolean);
        if (!valid.length) {
          addMsg({
            type: "text",
            sender: "bot",
            text: "Nessun pagamento trovato per questo utente.",
          });
          return;
        }
        let risposta = `💳 Pagamenti di ${user.firstName || ""} ${user.lastName || ""} (${valid.length}):\n\n`;
        valid.slice(0, 15).forEach((p, idx) => {
          const titolo = p.titolo || `Pagamento ${idx + 1}`;
          const dovuto =
            p.importoDovuto != null
              ? p.importoDovuto.toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                }) + " €"
              : null;
          const pagato =
            p.importoTotalePagato != null
              ? p.importoTotalePagato.toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                }) + " €"
              : null;
          const statoLabel =
            p.stato != null ? (p.stato !== 0 ? "Pagato" : "Non pagato") : "";
          const desc = p.descrizione || "";
          risposta += `${idx + 1}. ${titolo}\n`;
          if (dovuto) risposta += `   Importo dovuto: ${dovuto}\n`;
          if (pagato) risposta += `   Importo pagato: ${pagato}\n`;
          if (statoLabel) risposta += `   Stato: ${statoLabel}\n`;
          if (desc) risposta += `   Note: ${desc}\n`;
          risposta += "\n";
        });
        if (valid.length > 15)
          risposta += `…e altri ${valid.length - 15} pagamenti.`;
        addMsg({ type: "text", sender: "bot", text: risposta.trimEnd() });
      } catch {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non riesco a recuperare i pagamenti al momento.",
        });
      }
    },
    [apiToken, addMsg],
  );

  const handleAnagraficaIntent = useCallback(
    async (user) => {
      if (!user) {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non ho un utente attivo. Inserisci prima un codice fiscale.",
        });
        return;
      }
      let risposta = `👤 Dati anagrafici di ${user.firstName || ""} ${user.lastName || ""}:\n\n`;
      if (user.fiscalCode) risposta += `• Codice fiscale: ${user.fiscalCode}\n`;
      if (user.email) risposta += `• Email: ${user.email}\n`;
      if (user.phoneNumber) risposta += `• Telefono: ${user.phoneNumber}\n`;
      if (user.city) risposta += `• Comune: ${user.city}\n`;
      if (user.address) risposta += `• Indirizzo: ${user.address}\n`;
      if (user.isEnabled !== undefined)
        risposta += `• Account attivo: ${user.isEnabled ? "Sì" : "No"}\n`;
      addMsg({ type: "text", sender: "bot", text: risposta });
    },
    [addMsg],
  );

  const handleAllegatiIntent = useCallback(
    async (user) => {
      if (!user) {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non ho un utente attivo. Inserisci prima un codice fiscale.",
        });
        return;
      }
      addMsg({
        type: "text",
        sender: "bot",
        text: `Sto cercando i documenti allegati di ${user.firstName || ""} ${user.lastName || ""}…`,
      });
      try {
        const res = await Repo.getAllegatiUtente(apiToken, user.fiscalCode);
        const docs = res?.data || [];
        const list = Array.isArray(docs) ? docs.filter(Boolean) : [];
        if (!list.length) {
          addMsg({
            type: "text",
            sender: "bot",
            text: "Nessun documento allegato trovato per questo utente.",
          });
          return;
        }
        let risposta = `📎 Documenti di ${user.firstName || ""} ${user.lastName || ""} (${list.length}):\n\n`;
        list.forEach((d, idx) => {
          const nomeRaw =
            d.nomeFile ?? d.fileName ?? d.name ?? `Documento ${idx + 1}`;
          const nome = nomeRaw.includes(";") ? nomeRaw.split(";")[0] : nomeRaw;
          const descrizione = d.descrizione ?? d.description ?? "";
          risposta += `${idx + 1}. ${nome}\n`;
          if (descrizione) risposta += `   Descrizione: ${descrizione}\n`;
          risposta += "\n";
        });
        addMsg({ type: "text", sender: "bot", text: risposta.trimEnd() });
      } catch {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non riesco a recuperare i documenti al momento.",
        });
      }
    },
    [apiToken, addMsg],
  );

  const handleStatisticheIntent = useCallback(
    async (user, cf) => {
      if (!user) {
        addMsg({
          type: "text",
          sender: "bot",
          text: "Non ho un utente attivo. Inserisci prima un codice fiscale.",
        });
        return;
      }
      addMsg({
        type: "text",
        sender: "bot",
        text: `Sto raccogliendo il quadro completo per ${user.firstName || ""} ${user.lastName || ""}…`,
      });

      const results = await Promise.allSettled([
        Repo.getIseeListValidated(apiToken),
        Repo.getSubscriptionUser(apiToken, user.id),
        Repo.getPagamentiUtentiOperator(apiToken, user.fiscalCode),
        Repo.getAllegatiUtente(apiToken, user.fiscalCode),
      ]);

      const safeArr = (res) => {
        if (res.status !== "fulfilled") return [];
        const d = res.value?.data;
        return Array.isArray(d) ? d.filter(Boolean) : d ? [d] : [];
      };

      const allIsee = safeArr(results[0]).filter(
        (i) =>
          i.user?.fiscalCode?.toUpperCase() === user.fiscalCode?.toUpperCase(),
      );
      const iscrizioni = safeArr(results[1]);
      const pagamenti = safeArr(results[2]);
      const allegati = safeArr(results[3]);

      const latestIsee = allIsee.sort(
        (a, b) => (b.year ?? 0) - (a.year ?? 0),
      )[0];
      const iseeVal = latestIsee ? latestIsee.value : null;
      const pagati = pagamenti.filter(
        (p) => p.stato != null && p.stato !== 0,
      ).length;
      const nonPagati = pagamenti.filter((p) => p.stato === 0).length;

      let risposta = `📊 Quadro completo — ${user.firstName || ""} ${user.lastName || ""}\n`;
      risposta += `${"─".repeat(38)}\n`;
      risposta += `👤 Anagrafici\n`;
      risposta += `   ${user.firstName || "—"} ${user.lastName || "—"} · CF: ${cf}\n`;
      if (user.city) risposta += `   Comune: ${user.city}\n`;
      risposta += `   Account: ${user.isEnabled ? "attivo ✔" : "disabilitato ✖"}\n\n`;

      risposta += `💶 ISEE\n`;
      if (latestIsee) {
        risposta += `   Valore: ${typeof iseeVal === "number" ? iseeVal.toLocaleString("it-IT", { minimumFractionDigits: 2 }) + " €" : (iseeVal ?? "—")}\n`;
        const anno = latestIsee.year;
        if (anno) risposta += `   Anno: ${anno}\n`;
        if (allIsee.length > 1)
          risposta += `   (${allIsee.length} attestazioni totali)\n`;
      } else {
        risposta += `   Nessun ISEE validato\n`;
      }
      risposta += "\n";

      risposta += `📋 Iscrizioni: ${iscrizioni.length}\n`;
      if (iscrizioni.length) {
        const per_stato = iscrizioni.reduce((acc, s) => {
          const k = STATE_LABELS[s.state] || s.state || "Altro";
          acc[k] = (acc[k] || 0) + 1;
          return acc;
        }, {});
        Object.entries(per_stato).forEach(([k, v]) => {
          risposta += `   ${k}: ${v}\n`;
        });
      }
      risposta += "\n";

      risposta += `💳 Pagamenti: ${pagamenti.length}`;
      if (pagamenti.length)
        risposta += ` (pagati: ${pagati} · non pagati: ${nonPagati})`;
      risposta += "\n\n";

      risposta += `📎 Documenti allegati: ${allegati.length}\n`;

      addMsg({ type: "text", sender: "bot", text: risposta.trimEnd() });
    },
    [apiToken, addMsg],
  );

  // ── Input processing ─────────────────────────────────────────
  const processInput = useCallback(
    async (raw) => {
      const text = raw.trim().toUpperCase();
      const rawLower = raw.trim().toLowerCase();

      // Commands
      if (
        text === "AIUTO" ||
        text === "HELP" ||
        normalize(rawLower) === "aiuto"
      ) {
        await simulateDelay(600);
        addMsg({ type: "text", sender: "bot", text: HELP_TEXT });
        return;
      }

      if (
        ["NUOVO", "RESET", "NUOVA RICERCA", "CAMBIA", "ALTRO UTENTE"].includes(
          text,
        ) ||
        normalize(rawLower).match(
          /^(nuovo|reset|cambia|altra ricerca|nuova ricerca|altro utente)$/,
        )
      ) {
        setActiveUser(null);
        setActiveCf(null);
        setActiveDecoded(null);
        await simulateDelay(500);
        addMsg({
          type: "text",
          sender: "bot",
          text: "Contesto azzerato. Inserisci il codice fiscale da analizzare:",
        });
        return;
      }

      // Dispatcher per intent singolo o multiplo
      const dispatchIntent = async (intentKey) => {
        if (intentKey === "isee") {
          await handleIseeIntent(activeUser);
          return true;
        }
        if (intentKey === "iscrizioni") {
          await handleIscrizioniIntent(activeUser);
          return true;
        }
        if (intentKey === "pagamenti") {
          await handlePagamentiIntent(activeUser);
          return true;
        }
        if (intentKey === "anagrafica") {
          await handleAnagraficaIntent(activeUser);
          return true;
        }
        if (intentKey === "allegati") {
          await handleAllegatiIntent(activeUser);
          return true;
        }
        if (intentKey === "statistiche") {
          await handleStatisticheIntent(activeUser, activeCf);
          return true;
        }
        return false;
      };

      // ── Follow-up: se c'è un utente attivo e la domanda non è un CF ──
      if (activeUser && !CF_REGEX.test(text)) {
        const intent = detectIntent(rawLower);
        await simulateDelay(500);

        // Multi-intent: es. "iscrizioni e pagamenti"
        if (Array.isArray(intent)) {
          for (const key of intent) {
            await dispatchIntent(key);
            await simulateDelay(300);
          }
          return;
        }

        if (intent && (await dispatchIntent(intent))) return;

        // Risposta se non capisce: suggerisce basandosi su parole chiave vaghe
        const fallback =
          normalize(rawLower).length < 3
            ? "Messaggio troppo breve. Puoi chiedermi qualcosa in italiano."
            : `Non ho capito cosa vuoi sapere su ${activeUser.firstName || activeCf}.\n` +
              `Puoi chiedermi (in italiano):\n• ISEE\n• Iscrizioni ai servizi\n• Pagamenti\n• Dati anagrafici\n• Documenti allegati\n• Quadro completo\n\nOppure scrivi NUOVO per cambiare utente.`;
        addMsg({ type: "text", sender: "bot", text: fallback });
        return;
      }

      // ── Se non c'è utente attivo e non è un CF: prova a dare un suggerimento ──
      if (!CF_REGEX.test(text)) {
        await simulateDelay(700);
        const isSimilarCf = text.replace(/\s/g, "").length >= 10;
        addMsg({
          type: "text",
          sender: "bot",
          text: isSimilarCf
            ? `"${text}" sembra un codice fiscale incompleto o errato.\nIl formato corretto è 16 caratteri: 6 lettere + 2 cifre + 1 lettera + 2 cifre + 1 lettera + 3 cifre + 1 lettera.\nEsempio: RSSMRA80A01H501U`
            : `Inserisci un codice fiscale di 16 caratteri per iniziare.\nEsempio: RSSMRA80A01H501U`,
        });
        return;
      }

      // ── Nuova ricerca per CF ──
      const validation = validateCodiceFiscale(text);
      const decoded = decodeCodiceFiscale(text);

      await simulateDelay(400);
      addMsg({
        type: "text",
        sender: "bot",
        text: `Sto cercando informazioni per il codice fiscale ${text}…`,
      });

      let systemUser = null;
      try {
        const response = await Repo.getUsersList(apiToken, {});
        if (response?.data && Array.isArray(response.data)) {
          systemUser =
            response.data.find(
              (u) => u.fiscalCode && u.fiscalCode.toUpperCase() === text,
            ) || null;
        }
      } catch {
        // gracefully degrade
      }

      setActiveCf(text);
      setActiveDecoded(decoded);
      setActiveUser(systemUser);

      await simulateDelay(300);

      addMsg({
        type: "summary",
        sender: "bot",
        cf: text,
        decoded,
        systemUser,
        validation,
      });

      await simulateDelay(800);

      const followUp = systemUser
        ? `Ho trovato ${systemUser.firstName || ""} ${systemUser.lastName || ""} nel sistema.\n\nCosa vuoi sapere? Puoi chiedermi in italiano:\n• ISEE  •  Iscrizioni  •  Pagamenti\n• Dati anagrafici  •  Documenti  •  Quadro completo\n\nOppure clicca uno dei pulsanti qui sotto.`
        : `Nessun utente registrato con questo codice fiscale.\nHo comunque decodificato i dati anagrafici dal CF.\n\nVuoi cercarne un altro? Scrivi NUOVO.`;

      addMsg({ type: "text", sender: "bot", text: followUp });
    },
    [
      apiToken,
      addMsg,
      activeUser,
      activeCf,
      activeDecoded,
      handleIseeIntent,
      handleIscrizioniIntent,
      handlePagamentiIntent,
      handleAnagraficaIntent,
      handleAllegatiIntent,
      handleStatisticheIntent,
    ],
  );

  // ── Send handler ─────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    addMsg({ type: "text", sender: "user", text: trimmed.toUpperCase() });
    setInput("");
    setLoading(true);

    const typingId = addTyping();

    try {
      await processInput(trimmed);
    } finally {
      removeTyping(typingId);
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [input, loading, addMsg, addTyping, removeTyping, processInput]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="chatbot-wrapper">
      {/* Header */}
      <div className="chatbot-header">
        <div className="chatbot-header-avatar">🤖</div>
        <div className="chatbot-header-info">
          <h5>Assistente CF</h5>
          <small>Ricerca utenti per codice fiscale</small>
        </div>
        <span className="chatbot-header-badge">● Online</span>
      </div>

      {/* Messages */}
      <div className="chatbot-messages" ref={messagesRef}>
        {messages.map((msg) => {
          if (msg.type === "typing") return <TypingBubble key={msg.id} />;
          if (msg.type === "summary") {
            return (
              <SummaryCard
                key={msg.id}
                cf={msg.cf}
                decoded={msg.decoded}
                systemUser={msg.systemUser}
                validation={msg.validation}
              />
            );
          }
          return (
            <TextBubble
              key={msg.id}
              text={msg.text}
              sender={msg.sender}
              time={msg.time}
            />
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="chatbot-quick-actions">
        <button
          className="quick-action-btn"
          onClick={() => {
            setInput("NUOVO");
            setTimeout(handleSend, 0);
          }}
          disabled={loading}
        >
          🔄 Nuovo CF
        </button>
        {activeUser && (
          <>
            <button
              className="quick-action-btn"
              onClick={() => {
                setInput("ISEE");
                setTimeout(handleSend, 0);
              }}
              disabled={loading}
            >
              📊 ISEE
            </button>
            <button
              className="quick-action-btn"
              onClick={() => {
                setInput("iscrizioni");
                setTimeout(handleSend, 0);
              }}
              disabled={loading}
            >
              📋 Iscrizioni
            </button>
            <button
              className="quick-action-btn"
              onClick={() => {
                setInput("pagamenti");
                setTimeout(handleSend, 0);
              }}
              disabled={loading}
            >
              💳 Pagamenti
            </button>
            <button
              className="quick-action-btn"
              onClick={() => {
                setInput("dati anagrafici");
                setTimeout(handleSend, 0);
              }}
              disabled={loading}
            >
              👤 Anagrafica
            </button>
            <button
              className="quick-action-btn"
              onClick={() => {
                setInput("documenti allegati");
                setTimeout(handleSend, 0);
              }}
              disabled={loading}
            >
              📎 Documenti
            </button>
            <button
              className="quick-action-btn"
              onClick={() => {
                setInput("quadro completo");
                setTimeout(handleSend, 0);
              }}
              disabled={loading}
            >
              📊 Quadro completo
            </button>
          </>
        )}
        <button
          className="quick-action-btn"
          onClick={() => {
            setInput("AIUTO");
            setTimeout(handleSend, 0);
          }}
          disabled={loading}
        >
          ❓ Aiuto
        </button>
      </div>

      {/* Input */}
      <div className="chatbot-input-area">
        <input
          ref={inputRef}
          type="text"
          className="chatbot-input"
          value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          placeholder={
            activeUser ? "Chiedi qualcosa sull'utente…" : "Es. RSSMRA80A01H501U"
          }
          disabled={loading}
          maxLength={activeUser ? 120 : 16}
          autoComplete="off"
          aria-label="Inserisci codice fiscale"
        />
        <button
          className="chatbot-send-btn"
          onClick={handleSend}
          disabled={loading || !input.trim()}
          aria-label="Invia"
          title="Invia (Invio)"
        >
          ➤
        </button>
      </div>
    </div>
  );
}

export default ChatbotCF;
